import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { assessmentsAPI } from '../lib/api';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';

export default function AssessmentResultsPage() {
  const { assessmentId } = useParams();
  const navigate = useNavigate();
  const [assessment, setAssessment] = useState(null);
  const [attempts, setAttempts] = useState([]);
  const [selectedAttempt, setSelectedAttempt] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    try {
      const [aRes, attRes] = await Promise.all([
        assessmentsAPI.get(assessmentId),
        assessmentsAPI.listAttempts(assessmentId)
      ]);
      setAssessment(aRes.data);
      setAttempts(attRes.data);
      if (attRes.data.length > 0) setSelectedAttempt(attRes.data[0]);
    } catch (err) {
      toast.error('Failed to load results');
    } finally {
      setLoading(false);
    }
  }, [assessmentId]);

  useEffect(() => { loadData(); }, [loadData]);

  // Auto-refresh while scoring
  useEffect(() => {
    const hasScoring = attempts.some(a => ['submitted', 'scoring'].includes(a.status));
    if (hasScoring) {
      const interval = setInterval(async () => {
        try {
          const res = await assessmentsAPI.listAttempts(assessmentId);
          setAttempts(res.data);
          if (selectedAttempt) {
            const updated = res.data.find(a => a._id === selectedAttempt._id);
            if (updated) setSelectedAttempt(updated);
          }
        } catch (err) {
          if (process.env.NODE_ENV === 'development') console.error('Poll attempts failed:', err);
        }
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [attempts, assessmentId, selectedAttempt]);

  const getScoreColor = (pct) => {
    if (pct >= 85) return '#10B981';
    if (pct >= 70) return '#0076A3';
    if (pct >= 50) return '#F59E0B';
    return '#EF4444';
  };

  const getRiskColor = (level) => {
    if (level === 'low') return '#10B981';
    if (level === 'medium') return '#F59E0B';
    return '#EF4444';
  };

  if (loading) {
    return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: 'hsl(var(--muted-foreground))' }}>Loading results...</div>;
  }

  if (!assessment) {
    return <div style={{ textAlign: 'center', padding: '4rem', color: 'hsl(var(--muted-foreground))' }}>Assessment not found</div>;
  }

  return (
    <div data-testid="assessment-results-page">
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', color: 'hsl(var(--muted-foreground))', cursor: 'pointer', padding: '0.25rem' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          <div>
            <h1 className="page-title">{assessment.title}</h1>
            <p className="page-subtitle">{attempts.length} attempt{attempts.length !== 1 ? 's' : ''}</p>
          </div>
        </div>
      </div>

      {attempts.length === 0 ? (
        <div className="empty-state" style={{ paddingTop: '4rem' }}>
          <p>No attempts yet. Share the assessment link with candidates.</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: '1.5rem' }}>
          {/* Left: Attempt list */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {attempts.map((att) => (
              <Card
                key={att._id}
                onClick={() => setSelectedAttempt(att)}
                style={{
                  background: selectedAttempt?._id === att._id ? 'rgba(240, 80, 0, 0.08)' : 'hsl(var(--card))',
                  border: `1px solid ${selectedAttempt?._id === att._id ? 'rgba(240, 80, 0, 0.3)' : 'hsl(var(--border))'}`,
                  borderRadius: 12, padding: '1rem', cursor: 'pointer'
                }}
                className="candidate-row"
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.375rem' }}>
                  <span style={{ fontSize: '0.875rem', fontWeight: 600 }}>{att.candidate_name}</span>
                  {att.percentage !== null && att.percentage !== undefined ? (
                    <span style={{ fontFamily: 'JetBrains Mono', fontSize: '0.875rem', fontWeight: 600, color: getScoreColor(att.percentage) }}>{att.percentage}%</span>
                  ) : (
                    <span style={{ fontSize: '0.6875rem', color: '#F59E0B' }}>{att.status}</span>
                  )}
                </div>
                <div style={{ fontSize: '0.6875rem', color: 'hsl(var(--muted-foreground))' }}>{att.candidate_email}</div>
              </Card>
            ))}
          </div>

          {/* Right: Selected attempt details */}
          {selectedAttempt && (
            <div>
              {['submitted', 'scoring'].includes(selectedAttempt.status) ? (
                <Card style={{ background: 'rgba(245, 158, 11, 0.08)', border: '1px solid rgba(245, 158, 11, 0.2)', borderRadius: 14, padding: '2rem', textAlign: 'center' }}>
                  <span className="status-dot status-processing" style={{ display: 'inline-block', marginBottom: '1rem' }} />
                  <h3 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1.125rem', fontWeight: 600, color: '#D97706', marginBottom: '0.5rem' }}>Scoring in progress...</h3>
                  <p style={{ fontSize: '0.8125rem', color: 'hsl(var(--muted-foreground))' }}>AI is evaluating the answers. This may take a minute.</p>
                </Card>
              ) : selectedAttempt.status === 'scored' ? (
                <Tabs defaultValue="overview">
                  <TabsList style={{ background: 'hsl(var(--card))', borderRadius: 10, marginBottom: '1.25rem' }}>
                    <TabsTrigger value="overview" style={{ borderRadius: 8 }}>Overview</TabsTrigger>
                    <TabsTrigger value="answers" style={{ borderRadius: 8 }}>Answers ({(selectedAttempt.scores || []).length})</TabsTrigger>
                    <TabsTrigger value="cheating" style={{ borderRadius: 8 }}>Integrity ({(selectedAttempt.anti_cheat_events || []).length})</TabsTrigger>
                  </TabsList>

                  <TabsContent value="overview">
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                      {/* Score */}
                      <Card style={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 14, padding: '1.5rem', textAlign: 'center' }}>
                        <div style={{ width: 80, height: 80, borderRadius: '50%', border: `3px solid ${getScoreColor(selectedAttempt.percentage)}`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 0.75rem', fontFamily: 'Cabinet Grotesk', fontSize: '1.75rem', fontWeight: 700, color: getScoreColor(selectedAttempt.percentage) }}>
                          {selectedAttempt.percentage}%
                        </div>
                        <div style={{ fontSize: '0.875rem', fontWeight: 600 }}>{selectedAttempt.total_score}/{selectedAttempt.total_possible} points</div>
                        <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.25rem' }}>
                          {selectedAttempt.summary?.overall_assessment || ''}
                        </div>
                      </Card>

                      {/* Integrity */}
                      <Card data-testid="anti-cheat-monitor-panel" style={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 14, padding: '1.5rem' }}>
                        <h4 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '0.9375rem', fontWeight: 600, marginBottom: '1rem' }}>Integrity Report</h4>
                        {selectedAttempt.summary?.integrity_assessment ? (
                          <>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                              <span style={{ width: 10, height: 10, borderRadius: '50%', background: getRiskColor(selectedAttempt.summary.integrity_assessment.risk_level) }} />
                              <span style={{ fontSize: '0.875rem', fontWeight: 600, color: getRiskColor(selectedAttempt.summary.integrity_assessment.risk_level), textTransform: 'capitalize' }}>
                                {selectedAttempt.summary.integrity_assessment.risk_level} risk
                              </span>
                            </div>
                            <p style={{ fontSize: '0.8125rem', color: 'hsl(var(--muted-foreground))', lineHeight: 1.6 }}>
                              {selectedAttempt.summary.integrity_assessment.summary}
                            </p>
                            <div style={{ marginTop: '0.75rem', fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>
                              {(selectedAttempt.anti_cheat_events || []).length} events logged
                            </div>
                          </>
                        ) : (
                          <p style={{ fontSize: '0.8125rem', color: 'hsl(var(--muted-foreground))' }}>No integrity data available</p>
                        )}
                      </Card>
                    </div>

                    {/* Skill breakdown */}
                    {selectedAttempt.summary?.skill_breakdown && (
                      <Card style={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 14, padding: '1.5rem', marginBottom: '1.5rem' }}>
                        <h4 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '0.9375rem', fontWeight: 600, marginBottom: '1rem' }}>Skill Breakdown</h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                          {Object.entries(selectedAttempt.summary.skill_breakdown).map(([skill, data]) => (
                            <div key={skill}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                                <span style={{ fontSize: '0.8125rem', fontWeight: 500 }}>{skill}</span>
                                <span style={{ fontFamily: 'JetBrains Mono', fontSize: '0.8125rem', color: getScoreColor(data.score) }}>
                                  {data.score}/{data.max}
                                </span>
                              </div>
                              <div className="score-progress">
                                <div className="score-progress-fill" style={{ width: `${(data.score / (data.max || 1)) * 100}%`, background: getScoreColor(data.score) }} />
                              </div>
                              {data.assessment && <p style={{ fontSize: '0.6875rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.25rem' }}>{data.assessment}</p>}
                            </div>
                          ))}
                        </div>
                      </Card>
                    )}

                    {/* Summary */}
                    {selectedAttempt.summary?.summary && (
                      <Card style={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 14, padding: '1.5rem' }}>
                        <h4 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '0.9375rem', fontWeight: 600, marginBottom: '0.75rem' }}>AI Summary</h4>
                        <p style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))', lineHeight: 1.7 }}>{selectedAttempt.summary.summary}</p>
                        {selectedAttempt.summary.recommendation && (
                          <div style={{ marginTop: '1rem', padding: '0.75rem', borderRadius: 10, background: 'rgba(240, 80, 0, 0.08)', border: '1px solid rgba(240, 80, 0, 0.15)', fontSize: '0.8125rem' }}>
                            <strong style={{ color: '#0076A3' }}>Recommendation:</strong> {selectedAttempt.summary.recommendation}
                          </div>
                        )}
                      </Card>
                    )}
                  </TabsContent>

                  <TabsContent value="answers">
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                      {(assessment.questions || []).map((q, i) => {
                        const score = (selectedAttempt.scores || [])[i] || {};
                        const answer = (selectedAttempt.answers || [])[i];
                        const timeSpent = (selectedAttempt.time_per_question || [])[i] || 0;
                        return (
                          <Card key={q.id || `q-${i}`} style={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 12, padding: '1.25rem' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <Badge style={{ background: 'rgba(240, 80, 0, 0.1)', color: '#0076A3', border: '1px solid rgba(240, 80, 0, 0.2)', borderRadius: 999, fontSize: '0.625rem' }}>{q.skill}</Badge>
                                <span style={{ fontSize: '0.6875rem', color: 'hsl(var(--muted-foreground))' }}>{q.type?.replace('_', ' ')} &middot; {q.difficulty}</span>
                              </div>
                              <span style={{ fontFamily: 'JetBrains Mono', fontSize: '0.8125rem', fontWeight: 600, color: getScoreColor((score.score / (score.max_points || 1)) * 100) }}>
                                {score.score ?? '?'}/{score.max_points ?? q.points}
                              </span>
                            </div>
                            <p style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem' }}>{q.question}</p>
                            <div style={{ padding: '0.75rem', borderRadius: 8, background: '#FAF7F2', fontSize: '0.8125rem', color: 'hsl(var(--muted-foreground))', marginBottom: '0.5rem', whiteSpace: 'pre-wrap', maxHeight: 200, overflow: 'auto' }}>
                              {answer || '(No answer)'}
                            </div>
                            {score.feedback && (
                              <p style={{ fontSize: '0.75rem', color: '#A1A1AA', fontStyle: 'italic' }}>{score.feedback}</p>
                            )}
                            <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem', fontSize: '0.6875rem', color: 'hsl(var(--muted-foreground))' }}>
                              <span>Time: {timeSpent}s</span>
                              {score.ai_generated_likelihood > 0.3 && (
                                <span style={{ color: '#F59E0B' }}>AI likelihood: {(score.ai_generated_likelihood * 100).toFixed(0)}%</span>
                              )}
                            </div>
                          </Card>
                        );
                      })}
                    </div>
                  </TabsContent>

                  <TabsContent value="cheating">
                    <Card data-testid="anti-cheat-monitor-panel" style={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 14, padding: '1.5rem' }}>
                      <h4 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '0.9375rem', fontWeight: 600, marginBottom: '1rem' }}>Anti-Cheat Event Log</h4>
                      {(selectedAttempt.anti_cheat_events || []).length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '2rem', color: 'hsl(var(--muted-foreground))' }}>
                          <p style={{ color: '#10B981', fontWeight: 600, marginBottom: '0.25rem' }}>Clean assessment</p>
                          <p style={{ fontSize: '0.8125rem' }}>No suspicious events detected.</p>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                          {(selectedAttempt.anti_cheat_events || []).map((evt, i) => (
                            <div data-testid="anti-cheat-event-row" key={`cheat-${evt.type}-${i}`} style={{
                              display: 'flex', alignItems: 'center', gap: '0.75rem',
                              padding: '0.625rem 0.875rem', borderRadius: 8,
                              background: '#FAF7F2', border: '1px solid hsl(var(--border))',
                              fontSize: '0.8125rem'
                            }}>
                              <Badge style={{
                                background: evt.type === 'tab_switch' ? 'rgba(239, 68, 68, 0.15)' : evt.type === 'copy_paste' ? 'rgba(245, 158, 11, 0.15)' : 'rgba(124, 58, 237, 0.15)',
                                color: evt.type === 'tab_switch' ? '#DC2626' : evt.type === 'copy_paste' ? '#D97706' : '#0076A3',
                                border: 'none', borderRadius: 999, fontSize: '0.625rem'
                              }}>
                                {evt.type?.replace('_', ' ')}
                              </Badge>
                              <span style={{ flex: 1, color: 'hsl(var(--muted-foreground))' }}>{evt.details}</span>
                              <span style={{ fontSize: '0.6875rem', color: 'hsl(var(--muted-foreground))' }}>Q{(evt.question_index || 0) + 1}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </Card>
                  </TabsContent>
                </Tabs>
              ) : (
                <Card style={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 14, padding: '2rem' }}>
                  <p style={{ color: 'hsl(var(--muted-foreground))' }}>Status: {selectedAttempt.status}</p>
                </Card>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
